import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import {
  buildSignatureRoleOptions,
} from '../../../../../../../pages/site-details-page/tab-screens/daily-report/daily-report-components/detail-components/control-forms-details/control-forms-details/control-forms-details.component';
import { ProjectsService } from '../../../../../../../core/projects.service';

/**
 * Read-only preview of a form definition (control_form), framed as a phone.
 *
 * Rather than describing a form in a table of field names, this renders it with
 * the SAME components the daily report uses, so what an admin sees here is what
 * an inspector fills in — and at the width they fill it in at:
 *
 *   display_format 'generic'        -> app-generic-form
 *   display_widget 'testing_forms'  -> app-testing-from
 *   everything else                 -> app-control-forms-details
 *
 * (that mapping mirrors the DWR's own dispatch in daily-report.component.ts).
 *
 * The phone screen scrolls on its own, so a long form is read by scrolling
 * inside the frame rather than moving the page. Tapping a question block pushes
 * a questions screen INSIDE the frame — the field app navigates to its own
 * QuestionsScreen there, and the renderers' web dialog would break out of the
 * phone entirely.
 *
 * The renderers are fed a blank form instance — no field values, no lines, no
 * signatures. Nothing is persisted: they only ever save when the DWR fires
 * `DailyReportService.triggerSave`, which this screen never does. They do flag
 * the project as having a dirty form when their controls initialize, so this
 * component clears that flag on destroy.
 */
@Component({
  selector: 'app-form-preview',
  templateUrl: './form-preview.component.html',
  styleUrls: ['./form-preview.component.scss'],
  standalone: false,
})
export class FormPreviewComponent implements OnChanges, OnDestroy {
  @Input() form: any;
  @ViewChild('scroller') scroller: ElementRef<HTMLDivElement>;

  public previewData: any = null;
  public renderer: 'generic' | 'testing' | 'control' = 'control';

  // The pushed questions screen, mirroring the field app's: one question at a
  // time, a progress bar, and the form's response options as a segmented pill.
  public questionsView: { label: string, questions: any[], options: string[] } = null;
  public questionIndex = 0;
  public answers: Record<string, string> = {};

  // The pushed signature sheet, mirroring the field app's: Done, a Role picker,
  // then a typed name or a printed name over a signing pad.
  public signatureView = false;

  constructor(private projectsService: ProjectsService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.form) {
      this.build();
    }
  }

  ngOnDestroy(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  /**
   * Builds the blank instance the renderers hydrate from. They read
   * control_form / field_values / lines / signatures / pictures / videos /
   * station_range and assume the array fields exist, so all of them are seeded.
   */
  private build(): void {
    this.closePushedScreens();

    if (!this.form) {
      this.previewData = null;
      return;
    }

    this.renderer = this.resolveRenderer(this.form);
    this.previewData = {
      id: null,
      control_form: this.form,
      field_values: [],
      lines: [],
      signatures: [],
      pictures: [],
      videos: [],
      station_range: null,
      name: this.form?.name || 'Form',
    };

    // The renderer is recreated per form but the scroll container is not, so a
    // form picked while scrolled down would otherwise open part-way through.
    if (this.scroller?.nativeElement) {
      this.scroller.nativeElement.scrollTop = 0;
    }
  }

  /* ------------------------------------------------- pushed signature screen */

  openSignature(): void {
    this.signatureView = true;
  }

  closeSignature(): void {
    this.signatureView = false;
  }

  /** Roles offered on the sheet — the form's own, else the app's QA/QC default. */
  get signatureRoles(): string[] {
    return buildSignatureRoleOptions(this.form?.ui_config).map(o => o.label);
  }

  /** Draw-pad mode vs. a typed name, per the template. */
  get signaturePadEnabled(): boolean {
    return !!this.form?.ui_config?.signature_pad?.enabled;
  }

  get requirePrintedName(): boolean {
    return !!this.form?.ui_config?.signature_pad?.require_printed_name;
  }

  /* ------------------------------------------------- pushed questions screen */

  openQuestions(view: { label: string, questions: any[], options: string[] }): void {
    if (!view?.questions?.length) {
      return;
    }
    this.questionsView = view;
    this.questionIndex = 0;
    this.answers = {};
  }

  closeQuestions(): void {
    this.questionsView = null;
    this.questionIndex = 0;
    this.answers = {};
  }

  /** Back out of whichever screen is pushed. */
  private closePushedScreens(): void {
    this.closeQuestions();
    this.closeSignature();
  }

  get question(): any {
    return this.questionsView?.questions?.[this.questionIndex] || null;
  }

  get questionCount(): number {
    return this.questionsView?.questions?.length || 0;
  }

  get progress(): number {
    return this.questionCount ? ((this.questionIndex + 1) / this.questionCount) * 100 : 0;
  }

  answerFor(question: any): string {
    return this.answers[question?.id] || null;
  }

  /**
   * Preview answers live in memory only — they exist so the segmented control
   * behaves like the field app's, and are dropped when the screen closes.
   */
  answer(option: string): void {
    if (!this.question) {
      return;
    }
    this.answers[this.question.id] = option;
  }

  previousQuestion(): void {
    this.questionIndex = Math.max(0, this.questionIndex - 1);
  }

  nextQuestion(): void {
    this.questionIndex = Math.min(this.questionCount - 1, this.questionIndex + 1);
  }

  private resolveRenderer(form: any): 'generic' | 'testing' | 'control' {
    if (form?.display_format === 'generic') {
      return 'generic';
    }
    if (form?.display_widget === 'testing_forms') {
      return 'testing';
    }
    return 'control';
  }
}
